import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import config from '../../config/environment';

// Snow animation keyframes
const snowfall = keyframes`
  0% {
    transform: translateY(-10px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(360deg);
    opacity: 0;
  }
`;

const Snowflake = styled.div`
  position: absolute;
  top: -10px;
  left: ${props => props.left}%;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: white;
  border-radius: 50%;
  opacity: 0.8;
  animation: ${snowfall} ${props => props.duration}s linear infinite;
  animation-delay: ${props => props.delay}s;
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
`;

const NewsletterSection = styled.section`
  position: relative;
  margin: 0;
  padding: clamp(3.5rem, 7vw, 5.75rem) 0;
  border-top: 1px solid rgba(16, 29, 64, 0.07);
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
  background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.78) 0%,
      rgba(241, 239, 234, 0.96) 46%,
      rgba(255, 255, 255, 0.72) 100%
    ),
    radial-gradient(
      circle at 18% 50%,
      rgba(157, 109, 73, 0.14) 0,
      rgba(157, 109, 73, 0) 34%
    ),
    radial-gradient(
      circle at 82% 42%,
      rgba(16, 29, 64, 0.08) 0,
      rgba(16, 29, 64, 0) 30%
  );
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: min(88rem, calc(100% - 2rem));
    height: 1px;
    transform: translateX(-50%);
    background: linear-gradient(
      90deg,
      transparent,
      rgba(157, 109, 73, 0.36),
      transparent
    );
    opacity: 0.7;
  }

  &::before {
    top: clamp(1.5rem, 3vw, 2.25rem);
  }

  &::after {
    bottom: clamp(1.5rem, 3vw, 2.25rem);
  }
`;

const NewsletterBand = styled.div`
  position: relative;
  z-index: 1;
  width: min(100%, 1400px);
  margin: 0 auto;
  padding: 0 clamp(1rem, 5vw, 3rem);
`;

const NewsletterPanel = styled.div`
  width: 100%;
  padding: clamp(2rem, 4.5vw, 3.15rem) clamp(1.5rem, 4vw, 3rem);
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.82);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.84), rgba(255, 255, 255, 0.58)),
    rgba(255, 255, 255, 0.66);
  backdrop-filter: blur(22px) saturate(1.35);
  -webkit-backdrop-filter: blur(22px) saturate(1.35);
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.9) inset,
    0 24px 70px rgba(16, 29, 64, 0.1),
    0 8px 24px rgba(16, 29, 64, 0.06);
  display: grid;
  grid-template-columns: minmax(18rem, 0.72fr) minmax(0, 1.28fr);
  align-items: center;
  gap: clamp(1.75rem, 4vw, 3.5rem);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
    border-radius: 22px;
  }
`;

const TextContainer = styled.div`
  text-align: left;

  @media (max-width: 900px) {
    text-align: center;
  }

  h2 {
    font-family: ${props => props.theme.fonts.main};
    font-size: clamp(1.85rem, 4vw, 2.7rem);
    margin: 0;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    line-height: 1.12;
  }

  p {
    margin: 0.65rem 0 0;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: clamp(0.9rem, 1.65vw, 1.05rem);
    line-height: 1.55;
    color: rgba(16, 29, 64, 0.58);
    max-width: 34ch;

    @media (max-width: 900px) {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const FormContainer = styled.form`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
  align-items: stretch;
  gap: 0.65rem 0.75rem;
  min-width: 0;
  position: relative;
  padding-bottom: 0.25rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    button[type='submit'] {
      grid-column: 1 / -1;
      justify-content: center;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    width: 100%;
    max-width: 26rem;
    margin: 0 auto;
  }
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 0.75rem 1.1rem;
  border: 1px solid rgba(16, 29, 64, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  color: ${props => props.theme.colors.primary};
  font-size: 0.9375rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: rgba(16, 29, 64, 0.42);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.18);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 0.75rem 1.65rem;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  box-shadow: 0 4px 14px rgba(157, 109, 73, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(157, 109, 73, 0.42);
  }

  &:disabled {
    opacity: 0.72;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DefaultFormStatus = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  margin-top: 0.35rem;
  font-size: 0.875rem;
  line-height: 1.45;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-align: right;
  color: ${props => (props.$error ? '#c53030' : '#276749')};

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const StatusMessage = styled.div`
  position: absolute;
  bottom: -30px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.9rem;
  color: ${props => props.error ? '#E53E3E' : '#48BB78'};
  transition: all 0.3s ease;
`;

const WinterNewsletterContainer = styled.div`
  background: #101d40;
  padding: 3rem 4rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: auto;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  font-family: ${props => props.theme.fonts.main};
  
  @media (max-width: 1024px) {
    padding: 2.5rem;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
    padding: 2rem;
  }
`;

const WinterTextContainer = styled.div`
  position: relative;
  z-index: 1;
  
  h2 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 3.5rem;
    margin: 0;
    letter-spacing: 0.5px;
    font-weight: 600;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    margin: 1rem 0 0 0;
    font-size: 1.4rem;
    opacity: 0.9;
    letter-spacing: 0.3px;
    color: white;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
`;

const WinterFormContainer = styled.form`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-left: 2rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 1024px) {
    gap: 0.75rem;
    margin-left: 1rem;
  }
   
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const WinterInput = styled.input`
  padding: 1rem 1.2rem;
  width: 180px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  backdrop-filter: blur(5px);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
    font-family: ${props => props.theme.fonts.main};
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const WinterSubmitButton = styled.button`
  background: linear-gradient(135deg, #E53E3E 0%, #C53030 100%);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
    background: linear-gradient(135deg, #C53030 0%, #9B2C2C 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem;
  }
`;

const parseResponse = async (response) => {
  if (!response) {
    return { data: null, message: null };
  }

  try {
    const json = await response.clone().json();
    return {
      data: json,
      message: typeof json?.message === 'string' ? json.message : null
    };
  } catch {
    try {
      const text = await response.clone().text();
      return {
        data: null,
        message: text || null
      };
    } catch {
      return { data: null, message: null };
    }
  }
};

const CompactFormWrapper = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem 0.65rem;
  min-width: 0;
  width: 100%;

  input[type='email'],
  button[type='submit'] {
    grid-column: 1 / -1;
  }

  @media (max-width: 900px) {
    max-width: 26rem;
    margin: 0 auto;
  }
`;

const CompactFormStatus = styled.div`
  grid-column: 1 / -1;
  font-size: 0.8125rem;
  line-height: 1.45;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: ${props => (props.$error ? '#c53030' : '#276749')};
  text-align: left;

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const NewsletterSignUp = ({ tag, customTitle, customDescription, variant = 'default' }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    status: null
  });

  // Generate snowflakes for winter theme
  const [snowflakes, setSnowflakes] = useState([]);
  
  useEffect(() => {
    if (tag === 'Christmas 2025' || tag === 'Christmas 2026') {
      const flakes = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 5 + 5,
        delay: Math.random() * 5
      }));
      setSnowflakes(flakes);
    }
  }, [tag]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, status: null });

    try {
      const response = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': config.frontendUrl
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({
          ...formData,
          origin: window.location.origin,
          tags: tag ? [tag] : ['Newsletter']
        })
      });
      const { data, message } = await parseResponse(response);

      if (!response.ok || data?.success === false) {
        throw new Error(
          (data && typeof data.message === 'string' && data.message) ||
          message ||
          `Subscription failed${response.status ? ` (${response.status})` : ''}`
        );
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        status: data?.status ?? null
      });
      
      if (data?.status === 'new_subscription') {
        setFormData({ firstName: '', lastName: '', email: '' });
      }
    } catch (error) {
      setStatus({
        submitting: false,
        error: error.message || 'An error occurred while subscribing. Please try again.',
        success: false,
        status: null
      });
    }
  };

  if (variant === 'compact') {
    return (
      <CompactFormWrapper onSubmit={handleSubmit}>
        <Input
          type="text"
          name="firstName"
          placeholder="First name"
          value={formData.firstName}
          onChange={handleChange}
          autoComplete="given-name"
        />
        <Input
          type="text"
          name="lastName"
          placeholder="Last name"
          value={formData.lastName}
          onChange={handleChange}
          autoComplete="family-name"
        />
        <Input
          type="email"
          name="email"
          placeholder="Email address"
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
        />
        <SubmitButton type="submit" disabled={status.submitting}>
          {status.submitting ? 'Signing up…' : 'Be the first to know'}
        </SubmitButton>
        {status.error && <CompactFormStatus $error>{status.error}</CompactFormStatus>}
        {status.success && (
          <CompactFormStatus>
            {status.status === 'already_subscribed'
              ? 'You are already subscribed to our newsletter.'
              : status.status === 'pending_confirmation'
                ? 'Thank you — please check your email to confirm your subscription.'
                : status.status === 'new_subscription'
                  ? 'Thank you — you will be the first to hear what is coming.'
                  : 'Thank you for subscribing.'}
          </CompactFormStatus>
        )}
      </CompactFormWrapper>
    );
  }

  // Use winter theme for Christmas newsletter sign-ups
  if (tag === 'Christmas 2025' || tag === 'Christmas 2026') {
    return (
      <WinterNewsletterContainer>
        {snowflakes.map(flake => (
          <Snowflake
            key={flake.id}
            left={flake.left}
            size={flake.size}
            duration={flake.duration}
            delay={flake.delay}
          />
        ))}
        <WinterTextContainer>
          <h2>Keep up to date</h2>
          <p>Sign up to be the first to hear about what's coming this winter</p>
        </WinterTextContainer>
        
        <WinterFormContainer onSubmit={handleSubmit}>
          <WinterInput 
            type="text" 
            name="firstName"
            placeholder="First Name" 
            value={formData.firstName}
            onChange={handleChange}
          />
          <WinterInput 
            type="text" 
            name="lastName"
            placeholder="Last Name" 
            value={formData.lastName}
            onChange={handleChange}
          />
          <WinterInput 
            type="email" 
            name="email"
            placeholder="Email" 
            value={formData.email}
            onChange={handleChange}
          />
          <WinterSubmitButton 
            type="submit" 
            disabled={status.submitting}
          >
            {status.submitting ? 'SIGNING UP...' : 'SIGN UP'}
          </WinterSubmitButton>
          
          {status.error && (
            <StatusMessage error>{status.error}</StatusMessage>
          )}
          {status.success && (
            <StatusMessage>
              {status.status === 'already_subscribed' 
                ? 'You are already subscribed to our newsletter!'
                : status.status === 'pending_confirmation'
                  ? 'Thank you for subscribing! Please check your email to confirm your subscription.'
                  : status.status === 'new_subscription'
                    ? 'Thank you for subscribing! You have been successfully added to our newsletter.'
                    : 'Thank you for subscribing!'}
            </StatusMessage>
          )}
        </WinterFormContainer>
      </WinterNewsletterContainer>
    );
  }

  // Default theme for other pages
  return (
    <NewsletterSection>
      <NewsletterBand>
        <NewsletterPanel>
          <TextContainer>
            <h2>{customTitle || 'Keep up to date'}</h2>
            <p>{customDescription || 'Sign up to be the first to hear about upcoming events'}</p>
          </TextContainer>

          <FormContainer onSubmit={handleSubmit}>
            <Input
              type="text"
              name="firstName"
              placeholder="First name"
              value={formData.firstName}
              onChange={handleChange}
              autoComplete="given-name"
            />
            <Input
              type="text"
              name="lastName"
              placeholder="Last name"
              value={formData.lastName}
              onChange={handleChange}
              autoComplete="family-name"
            />
            <Input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />
            <SubmitButton type="submit" disabled={status.submitting}>
              {status.submitting ? 'Signing up…' : 'Sign up'}
            </SubmitButton>

            {status.error && <DefaultFormStatus $error>{status.error}</DefaultFormStatus>}
            {status.success && (
              <DefaultFormStatus>
                {status.status === 'already_subscribed'
                  ? 'You are already subscribed to our newsletter.'
                  : status.status === 'pending_confirmation'
                    ? 'Thank you — please check your email to confirm your subscription.'
                    : status.status === 'new_subscription'
                      ? 'Thank you — you have been added to our newsletter.'
                      : 'Thank you for subscribing.'}
              </DefaultFormStatus>
            )}
          </FormContainer>
        </NewsletterPanel>
      </NewsletterBand>
    </NewsletterSection>
  );
};

export default NewsletterSignUp;
